import { Box, Checkbox, Grid, Group, Select, Skeleton, Stack, TextInput } from '@mantine/core';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import countries from 'i18n-iso-countries';
import { tKeys } from 'locales';
import { useTranslation } from 'locales/logic';
import { useLoadCommunicationLocales } from 'modules/Settings/Configuration/api';
import { useHistory, useParams } from 'react-router-dom';
import { useCreateContactPerson } from 'shared/hooks/use-create-contact-person';
import { useUpdateContactPerson } from 'shared/hooks/use-update-contact-person';
import type { NotificationTypes } from 'types/notification-types';

import { type ContactPersonFormParams, useContactPersonForm } from '../hooks/useContactPersonForm';

import { MultiEmailInput } from './MultiEmailInput';
import { MultiPhoneInput } from './MultiPhoneInput';

export const ContactPersonForm = () => {
  const { communicationLanguages } = useLoadCommunicationLocales();
  const { t } = useTranslation();
  const history = useHistory();

  const { id: debtorId, contactPersonId } = useParams<ContactPersonFormParams>();

  const { createContactPerson, isCreateContactPersonLoading } = useCreateContactPerson();
  const { updateContactPerson, isUpdateContactPersonLoading } = useUpdateContactPerson();
  const { form, isEditMode, isContactPersonInitialLoading, getNotificationsItems, currentLang } =
    useContactPersonForm();

  const handleSaveClient = () => {
    if (isEditMode && contactPersonId) {
      updateContactPerson(
        { debtorId, contactPersonId, data: form.values },
        { onSuccess: () => history.goBack() },
      );
    } else
      createContactPerson({ debtorId, data: form.values }, { onSuccess: () => history.goBack() });
  };

  const countriesForSelect = Object.entries(countries.getNames(currentLang)).map(
    ([value, description]) => ({ value, description }),
  );

  const notificationsItems = getNotificationsItems();

  if (isContactPersonInitialLoading) {
    return (
      <>
        <PageTitle>
          <PageTitle.Back />
          <Skeleton height={24} width={300} />
        </PageTitle>
        <Stack>
          <Group grow>
            <Skeleton bdrs={16} height={400} />
            <Skeleton bdrs={16} height={400} />
          </Group>
          <Group align="top" wrap="nowrap">
            <Skeleton bdrs={16} height={100} width="50%" />
            <Box flex={1}>
              <Skeleton mb="sm" bdrs={16} height={200} />
              <Skeleton bdrs={16} height={200} />
            </Box>
          </Group>
        </Stack>
      </>
    );
  }

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        {isEditMode ? t(tKeys.CONTACT_PERSON.EDIT) : t(tKeys.CONTACT_PERSON.ADD)}
        <PageTitle.Actions>
          <SaveButton
            disabled={!form.isDirty()}
            loading={isUpdateContactPersonLoading || isCreateContactPersonLoading}
            form="clientForm"
            type="submit"
          />
        </PageTitle.Actions>
      </PageTitle>
      <form onSubmit={form.onSubmit(handleSaveClient)} id="clientForm">
        <Grid>
          <Grid.Col span={6}>
            <Panel title={t(tKeys.CONTACT_PERSON.BASIC_INFORMATION)} h="100%">
              <Stack>
                <Group grow>
                  <TextInput
                    required
                    key={form.key('first_name')}
                    label={t(tKeys.FORM.FIRSTNAME)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.FULL_NAME)}
                    {...form.getInputProps('first_name')}
                  />
                  <TextInput
                    required
                    key={form.key('last_name')}
                    label={t(tKeys.FORM.LASTNAME)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.FULL_NAME)}
                    {...form.getInputProps('last_name')}
                  />
                </Group>
                <TextInput
                  key={form.key('job_title')}
                  label={t(tKeys.FORM.JOB_TITLE)}
                  placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.JOB_TITLE)}
                  {...form.getInputProps('job_title')}
                />
                <Group align="end" grow>
                  <Select
                    key={form.key('locale')}
                    label={t(tKeys.FORM.CLIENT.CONTACT_LANG)}
                    data={communicationLanguages.map(lang => ({
                      value: lang.value,
                      label: lang.description,
                    }))}
                    clearable
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.LOCALE)}
                    {...form.getInputProps('locale')}
                  />
                  <Checkbox
                    mb="xs"
                    ta="right"
                    label={t(tKeys.CONTACT_PERSON.MAIN_CONTACT)}
                    {...form.getInputProps('main_contact', { type: 'checkbox' })}
                  />
                </Group>
              </Stack>
            </Panel>
          </Grid.Col>
          <Grid.Col span={6}>
            <Panel title={t(tKeys.ADDRESS)} h="100%">
              <Stack my="md">
                <Group grow>
                  <TextInput
                    label={t(tKeys.FORM.ADDRESS.STREET)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.STREET)}
                    {...form.getInputProps('address.street_name')}
                  />
                  <TextInput
                    label={t(tKeys.NUMBER)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.STREET_NUMBER)}
                    {...form.getInputProps('address.street_number')}
                  />
                  <TextInput
                    label={t(tKeys.FORM.ADDRESS.BOX)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.STREET_BOX)}
                    {...form.getInputProps('address.street_box')}
                  />
                </Group>
                <Group>
                  <TextInput
                    label={t(tKeys.FORM.ADDRESS.CITY)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.CITY)}
                    {...form.getInputProps('address.city')}
                  />
                  <TextInput
                    label={t(tKeys.FORM.ADDRESS.POSTAL_CODE)}
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.POSTAL_CODE)}
                    {...form.getInputProps('address.zip_code')}
                  />
                  <Select
                    key={form.key('address.country_code')}
                    flex={1}
                    label={t(tKeys.FORM.ADDRESS.COUNTRY)}
                    searchable
                    clearable
                    placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.COUNTRY)}
                    data={countriesForSelect.map(country => ({
                      value: country.value,
                      label: country.description,
                    }))}
                    {...form.getInputProps('address.country_code')}
                  />
                </Group>
                <TextInput
                  label={t(tKeys.ADDITIONAL_LINE_1)}
                  placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.ADDITIONAL_LINE_1)}
                  {...form.getInputProps('address.additional_line_1')}
                />
                <TextInput
                  label={t(tKeys.ADDITIONAL_LINE_2)}
                  placeholder={t(tKeys.FORM.CLIENT.PLACEHOLDER.ADDITIONAL_LINE_2)}
                  {...form.getInputProps('address.additional_line_2')}
                />
              </Stack>
            </Panel>
          </Grid.Col>
          <Grid.Col span={6}>
            <Panel title={t(tKeys.CONTACT_PERSON.NOTIFICATIONS_MEDIUM)}>
              <Checkbox.Group
                value={form.values.notifications}
                onChange={value =>
                  form.setFieldValue('notifications', value as NotificationTypes[])
                }
              >
                <Group align="top" grow>
                  <Stack>
                    {notificationsItems
                      .slice(0, Math.ceil(notificationsItems.length / 2))
                      .map(({ value, description }) => (
                        <Checkbox key={value} value={value} label={description} />
                      ))}
                  </Stack>
                  <Stack>
                    {notificationsItems
                      .slice(Math.ceil(notificationsItems.length / 2))
                      .map(({ value, description }) => (
                        <Checkbox key={value} value={value} label={description} />
                      ))}
                  </Stack>
                </Group>
              </Checkbox.Group>
            </Panel>
          </Grid.Col>
          <Grid.Col span={6}>
            <Stack>
              <MultiEmailInput form={form} />
              <MultiPhoneInput form={form} />
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </>
  );
};
